import React, { useState } from 'react'
import { Drawer } from '../../../../../components/elements/Drawer'
import useLayoutHook from '../../../../../hooks/layoutHook';
import useUserAccount from '../../../../../hooks/userAccountHook';
import { IActivityStreams, ITask } from '../../../../../resources/interfaces';
import { IUser, User } from '../../../../../models/User';
import TaskListCard from '../../../../../components/cards/TasksListCard';
import ActivityStepper from '../../../../../components/ActivityStepper';
import { getSupervisorSurbodinates, getAllTasks, getTaskById, getStaffActivities } from '../../../../../api/requests';
import { XMarkIcon } from '@heroicons/react/24/solid';
import UserListCard from '../../../../../components/cards/UserListCard';
import Button from '../../../../../components/form-control/Button';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { showToast } from '../../../../../utils/miscFunctions';


const DashboardSidebarContent = () => {
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
    const { user } = useUserAccount();
    const [myTasks, setMyTasks] = React.useState<ITask[]>([]);
    const [staffActivities, setStaffActivities] = React.useState<IActivityStreams[]>([]);
    const [supervisorSubordinates, setSupervisorSubordinates] = React.useState<User[]>([]);
    const { isMobile } = useLayoutHook();
    const [taskDetails, setTaskDetails] = useState<ITask | null>(null);
    const [isTaskDrawerOpen, setIsTaskDrawerOpen] = React.useState<boolean>(false);
    const [isLoadingTask, setIsLoadingTask] = React.useState<boolean>(false);
    const [taskError, setTaskError] = React.useState<string | null>(null);

    const getSupervisorStaff = async () => {
        try {
            const res = await getSupervisorSurbodinates();
            const _staff = res?.map((staff: IUser) => new User(staff));
            setSupervisorSubordinates(_staff);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchStaffActivities = async () => {
        try {
            const res: IActivityStreams[] = await getStaffActivities();
            setStaffActivities(res);
        } catch (error) {
            console.error(error);
        }
    };

    const getMyTasks = async () => {
        try {
            const res = await getAllTasks(0, 100);
            setMyTasks(res?.content);
        } catch (error) {
            console.error(error);
        }
    };

    const getATask = async (taskId: string) => {
        try {
            setIsLoadingTask(true);
            setTaskError(null);
            const response = await getTaskById(taskId);
            setTaskDetails(response);
            setIsTaskDrawerOpen(true);
        } catch (error: any) {
            const errorMessage = error?.description || error?.message || 'Failed to load task details';
            setTaskError(errorMessage);
            showToast.error(errorMessage);
        } finally {
            setIsLoadingTask(false);
        }
    };

    const closeTaskDrawer = () => {
        setIsTaskDrawerOpen(false);
        // Small delay before clearing task details to allow for smooth animation
        setTimeout(() => {
            setTaskDetails(null);
            setTaskError(null);
        }, 300);
    };


    const { isLoading: subordinatesLoading } = useQuery({
        queryKey: ['supervisorSubordinates'],
        queryFn: getSupervisorStaff
    });

    const { isLoading: staffActivityLoading } = useQuery({
        queryKey: ['staffActivities'],
        queryFn: fetchStaffActivities
    });

    const { } = useQuery({ queryKey: ['myTasks'], queryFn: getMyTasks });

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    return (
        <div className="relative">
            <div className="">
                {/* Toggle button */}
                <button
                    onClick={toggleDrawer}
                    className="fixed right-0 top-1/2 z-30 p-2 bg-primary-main text-white rounded-l-lg shadow-lg hover:bg-primary-main transition-colors"
                    style={{ transform: 'translateY(-50%)' }}
                >
                    {isDrawerOpen ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                        </svg>
                    )}
                </button>
            </div>

            {/* Drawer */}
            <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} position="right" size={isMobile ? 'full' : 600} height={isMobile ? 'full' : 'auto'}>
                <div className="sticky top-0 p-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-darkBg-mainText mb-4">Quick Access</h2>
                </div>

                <div className="space-y-4 p-4">
                    <TaskListCard
                        title="Tasks"
                        tasks={myTasks}
                        onclick={(taskId: string) => {
                            getATask(taskId)
                        }}
                        me={user}
                    />
                    <hr />

                    <ActivityStepper
                        activityStreams={staffActivities}
                        orientation="vertical"
                        currentStep={0}
                        isLoading={staffActivityLoading}
                        page="Dashboard"
                    />

                    <hr />
                    <UserListCard
                        title="Leaderboard"
                        users={supervisorSubordinates}
                        isLoading={subordinatesLoading}
                    />
                    <hr />

                    <UserListCard
                        title="My Team"
                        users={supervisorSubordinates}
                        isLoading={subordinatesLoading}
                    />
                </div>
            </Drawer>


            {/* Task Details Drawer */}
            <Drawer isOpen={isTaskDrawerOpen} onClose={closeTaskDrawer} position="right" size={isMobile ? 'full' : 600} height={isMobile ? 'full' : 'auto'}>
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-gray-900">
                            {isLoadingTask ? 'Loading...' : 'Task Details'}
                        </h2>
                        {/* <button
                            onClick={closeTaskDrawer}
                            className="text-gray-400 hover:text-gray-500 focus:outline-none"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button> */}
                    </div>

                    {isLoadingTask ? (
                        <div className="flex justify-center items-center h-64">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                        </div>
                    ) : taskError ? (
                        <div className="text-center py-10">
                            <p className="text-red-500 mb-4">{taskError}</p>
                            <Button onClick={() => getATask(taskDetails?.id || '')} />
                        </div>
                    ) : taskDetails ? (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">{taskDetails.name}</h3>
                                <div className="mt-2 text-sm text-gray-500">
                                    <p>{taskDetails.description || 'No description available'}</p>
                                </div>
                            </div>

                            <div className="border-t border-gray-200 pt-4">
                                <dl className="space-y-4">
                                    <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                                        <dt className="text-sm font-medium text-gray-500">Status</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${taskDetails.status === 'completed'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                {taskDetails.status}
                                            </span>
                                        </dd>
                                    </div>
                                    <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                                        <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">
                                            {taskDetails.assignedTo?.firstName} {taskDetails.assignedTo?.lastName}
                                        </dd>
                                    </div>
                                    <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                                        <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">
                                            {moment(taskDetails.dueOn).format('MMM D, YYYY h:mm A')}
                                        </dd>
                                    </div>
                                    <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                                        <dt className="text-sm font-medium text-gray-500">Type</dt>
                                        <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 capitalize">
                                            {taskDetails.type || '-'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            <div className="border-t border-gray-200 pt-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Comments</h4>
                                <div className="space-y-4">
                                    {taskDetails.comments?.length > 0 ? (
                                        taskDetails.comments.map((comment: any, index: number) => (
                                            <div key={index} className="flex space-x-3">
                                                <div className="flex-shrink-0">
                                                    <img
                                                        className="h-10 w-10 rounded-full"
                                                        src={comment.user?.avatar || `https://ui-avatars.com/api/?name=${comment.user?.name || 'U'}`}
                                                        alt=""
                                                    />
                                                </div>
                                                <div>
                                                    <div className="text-sm">
                                                        <p className="font-medium text-gray-900">
                                                            {comment.user?.name || 'Unknown User'}
                                                        </p>
                                                        <p className="text-gray-500">
                                                            {moment(comment.createdAt).fromNow()}
                                                        </p>
                                                    </div>
                                                    <div className="mt-1 text-sm text-gray-700">
                                                        <p>{comment.text}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-500">No comments yet</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-gray-500">No task selected</p>
                        </div>
                    )}
                </div>
            </Drawer>
        </div>
    )
}

export default DashboardSidebarContent